import React, { useCallback, useMemo } from "react";
import { SearchOption } from "@jsq/ds/core";
import { useGetEntityByArenaLazyQuery } from "#graphql/supergraphHooks";
import { clientContext, toDbId } from "#utils/graphql_utils";
import { CustomFilter } from "../CustomFilter";
import { EntityFilterProps } from "./types";

/**
 * The custom filtering component for filtering based on the entity name.
 * @param filterProps props fed by the AgGrid filtering model
 */
export const EntityFilter = (filterProps: EntityFilterProps) => {
  const { dependentFilter, ...restFilterProps } = filterProps;
  const [fetchEntityQuery, { data, loading }] = useGetEntityByArenaLazyQuery();

  const entityLists: SearchOption[] = useMemo(
    () =>
      data?.investmentEntities?.edges.map((entity) => ({
        id: entity.node.id,
        label: entity.node.name,
      })) ?? [],
    [data],
  );

  // Fetching the entity info based on the dependent(arenaId) and the search term.
  // EntitiesFilters.arenaIds expects DB ids (see EntitySelector), so the
  // dependent global id is converted with toDbId before being passed.
  const fetchEntityInfo = useCallback(
    async (dependentId: string, searchTerm: string) => {
      await fetchEntityQuery({
        context: clientContext(),
        variables: {
          arenaIds: dependentId ? [String(toDbId(dependentId))] : undefined,
          searchTerm,
        },
        fetchPolicy: "no-cache",
      });
    },
    [fetchEntityQuery],
  );

  return (
    <CustomFilter
      dependentFilter={dependentFilter}
      filterProps={restFilterProps}
      options={entityLists}
      isLoading={loading}
      infoText="Please select the client first"
      hintText="Search for an entity"
      // eslint-disable-next-line @typescript-eslint/no-misused-promises
      optionsFetcher={fetchEntityInfo}
    />
  );
};
